// Grammar and spelling checker that runs in the browser.
//
// What it does: a real English spelling dictionary (Hunspell en_US via nspell), a set of high-confidence
// grammar rules (contractions, their/they're, its/it's, "has went", a/an, a few agreement patterns) and
// spacing/capitalisation fixes. What it does not do: understand sentences the way a language model does,
// so anything it is not sure about is listed as a suggestion and left in the text unchanged.
//
// Important: URLs, e-mail addresses, file names, version numbers, decimals and abbreviations are masked
// before any rule runs, so correct text such as www.example.com/index.html is never altered.

export type GrammarIssue = { type: string; message: string };
export type SpellChecker = { correct(word: string): boolean; suggest(word: string): string[] };
export type EnglishVariant = "auto" | "us" | "uk";
export type GrammarOptions = {
  /** Spelling convention. UK and US spellings are NEVER changed in the text; "us" only lists UK spellings as suggestions. Default "auto". */
  variant?: EnglishVariant;
  /** Browser language (e.g. "en-GB"), used by "auto" only when the text itself gives no clue. */
  localeHint?: string;
};
export type GrammarResult = { corrected: string; issues: GrammarIssue[]; suggestions: GrammarIssue[]; /** The spelling convention the check used. */ variant?: "us" | "uk" };
export type GroupedIssue = { message: string; count: number };

/** Repeated identical messages collapsed into one entry with a count, in order of first appearance ("dont" → "don't" ×170). */
export function groupIssues(list: GrammarIssue[]): GroupedIssue[] {
  const byMessage = new Map<string, GroupedIssue>();
  for (const i of list) {
    const g = byMessage.get(i.message);
    if (g) g.count++;
    else byMessage.set(i.message, { message: i.message, count: 1 });
  }
  return Array.from(byMessage.values());
}

// --- UK / US spelling ---------------------------------------------------------------------------------
// Correct British spellings are not mistakes. They are never rewritten; a visitor who chose US English only
// gets a suggestion. UK -> US pairs below are the common ones; -our / -ise / -yse forms are recognised by pattern
// but only when the dictionary confirms the US form is a real word (so "tour", "advise" and "precise" never match).
const UK_TO_US: Record<string, string> = {
  colour: "color", flavour: "flavor", honour: "honor", favour: "favor", favourite: "favorite", behaviour: "behavior",
  neighbour: "neighbor", labour: "labor", harbour: "harbor", humour: "humor", rumour: "rumor", savour: "savor", vapour: "vapor",
  centre: "center", theatre: "theater", metre: "meter", litre: "liter", fibre: "fiber", calibre: "caliber", sombre: "somber",
  defence: "defense", licence: "license", offence: "offense", pretence: "pretense", cheque: "check", tyre: "tire",
  programme: "program", jewellery: "jewelry", travelled: "traveled", travelling: "traveling", traveller: "traveler",
  cancelled: "canceled", cancelling: "canceling", labelled: "labeled", labelling: "labeling", modelled: "modeled",
  modelling: "modeling", counselling: "counseling", counsellor: "counselor", fuelled: "fueled", fuelling: "fueling",
  levelled: "leveled", levelling: "leveling", signalled: "signaled", signalling: "signaling", marvellous: "marvelous",
  enrolment: "enrollment", fulfil: "fulfill", skilful: "skillful", judgement: "judgment", ageing: "aging",
  aluminium: "aluminum", paediatric: "pediatric", anaemia: "anemia", oestrogen: "estrogen", foetus: "fetus",
  manoeuvre: "maneuver", sceptic: "skeptic", sceptical: "skeptical", speciality: "specialty", plough: "plow",
  pyjamas: "pajamas", kerb: "curb", practise: "practice", grey: "gray", mum: "mom", mould: "mold", moustache: "mustache",
  storey: "story", cosy: "cozy", draught: "draft",
};
// US forms that are also ordinary words in UK English: never counted as evidence of US spelling.
const SHARED_US = new Set(["program", "check", "tire", "license", "practice", "story", "draft", "gray", "mom", "mold", "curb", "plow", "fetus", "estrogen"]);
const US_EVIDENCE = new Set(Object.values(UK_TO_US).filter((v) => !SHARED_US.has(v)));

/** The US spelling of a British one, or null when the word is not a known British variant. */
function ukToUs(word: string, spell?: SpellChecker | null): string | null {
  const lower = word.toLowerCase();
  const hit = UK_TO_US[lower];
  if (hit) return hit;
  if (!spell || lower.length < 6 || spell.correct(lower) || spell.correct(word)) return null;
  const candidates: string[] = [];
  if (/our(s|ed|ing|ful|able|less|ite|ist|ists)?$/.test(lower)) candidates.push(lower.replace(/our(s|ed|ing|ful|able|less|ite|ist|ists)?$/, "or$1"));
  if (/is(e|es|ed|ing|ation|ations|er|ers)$/.test(lower)) candidates.push(lower.replace(/is(e|es|ed|ing|ation|ations|er|ers)$/, "iz$1"));
  if (/ys(e|es|ed|ing)$/.test(lower)) candidates.push(lower.replace(/ys(e|es|ed|ing)$/, "yz$1"));
  return candidates.find((c) => spell.correct(c)) ?? null;
}

const UK_LOCALES = /^en[-_](gb|uk|au|nz|ie|in|za|sg|hk|my|pk|ng|ke)\b/i;
function resolveVariant(words: string[], spell: SpellChecker | null | undefined, opts: GrammarOptions): "us" | "uk" {
  const v = opts.variant ?? "auto";
  if (v === "us" || v === "uk") return v;
  let uk = 0, us = 0;
  for (const w of words) {
    if (ukToUs(w, spell)) uk++;
    else if (US_EVIDENCE.has(w.toLowerCase())) us++;
  }
  if (uk !== us) return uk > us ? "uk" : "us";
  return opts.localeHint && UK_LOCALES.test(opts.localeHint) ? "uk" : "us";
}


const COMMON_MISSPELLINGS: Record<string, string> = {
  teh: "the", recieve: "receive", seperate: "separate", definately: "definitely", occured: "occurred",
  untill: "until", wich: "which", alot: "a lot", becuase: "because", begining: "beginning", beleive: "believe",
  calender: "calendar", concious: "conscious", enviroment: "environment", goverment: "government",
  neccessary: "necessary", noticable: "noticeable", occassion: "occasion", publically: "publicly",
  recomend: "recommend", similiar: "similar", thier: "their", accomodate: "accommodate", tomorow: "tomorrow",
  tommorow: "tomorrow", tommorrow: "tomorrow", wierd: "weird", freind: "friend", adress: "address", sucess: "success",
  succesful: "successful", embarass: "embarrass", existance: "existence", foriegn: "foreign", grammer: "grammar",
  harrass: "harass", independant: "independent", knowlege: "knowledge", liason: "liaison", millenium: "millennium",
  mispell: "misspell", pursue: "pursue", refered: "referred", relevent: "relevant", resturant: "restaurant",
  rythm: "rhythm", scedule: "schedule", suprise: "surprise", truely: "truly", twelth: "twelfth", vaccum: "vacuum",
  writting: "writing", acheive: "achieve", agressive: "aggressive", apparantly: "apparently", basicly: "basically",
  buisness: "business", comming: "coming", completly: "completely", dissapoint: "disappoint", excersise: "exercise",
  finaly: "finally", gaurd: "guard", happend: "happened", immediatly: "immediately", intresting: "interesting",
  lenght: "length", peice: "piece", persue: "pursue", posession: "possession", prefered: "preferred",
  probaly: "probably", realy: "really", reccomend: "recommend", responsability: "responsibility", tounge: "tongue",
  usefull: "useful", wether: "whether", whereever: "wherever", yeild: "yield",
};

const CONTRACTIONS: Record<string, string> = {
  dont: "don't", doesnt: "doesn't", didnt: "didn't", isnt: "isn't", arent: "aren't", wasnt: "wasn't", werent: "weren't",
  cant: "can't", couldnt: "couldn't", wouldnt: "wouldn't", shouldnt: "shouldn't", wont: "won't", hasnt: "hasn't",
  havent: "haven't", hadnt: "hadn't", youre: "you're", theyre: "they're", thats: "that's", whats: "what's",
  ive: "I've", youve: "you've", weve: "we've", theyve: "they've", ill: "I'll", hes: "he's", shes: "she's",
};
// Words that are real words as well ("ill", "wont", "hes", "shes", "cant") are only fixed when unambiguous.
const AMBIGUOUS_CONTRACTIONS = new Set(["ill", "wont", "hes", "shes", "cant", "ive"]);

const IRREGULAR_FIXES: Record<string, string> = {
  buyed: "bought", goed: "went", runned: "ran", eated: "ate", taked: "took", maked: "made", thinked: "thought",
  bringed: "brought", teached: "taught", catched: "caught", knowed: "knew", growed: "grew", writed: "wrote",
  speaked: "spoke", breaked: "broke", choosed: "chose", drived: "drove", gived: "gave", seeed: "saw", sleeped: "slept",
};

const UNCOUNTABLE = "information|advice|equipment|furniture|knowledge|luggage|homework|feedback|software|news|research|evidence|traffic|weather|money";

const ABBREVIATIONS = /\b(?:e\.g|i\.e|etc|vs|mr|mrs|ms|dr|prof|sr|jr|st|inc|ltd|co|no|approx|a\.m|p\.m|u\.s|u\.k|u\.n|ph\.d)\./gi;
const PROTECTED = new RegExp([
  "`[^`\\n]*`", // inline code
  "\\b(?:https?:\\/\\/|ftp:\\/\\/|www\\.)[^\\s<>\"')]+", // URLs
  "[\\w.+-]+@[\\w-]+(?:\\.[\\w-]+)+", // e-mail
  "\\b[\\w-]+(?:\\.[\\w-]+)*\\.(?:com|org|net|io|co|edu|gov|app|dev|ai|us|uk|in|html?|php|js|jsx|ts|tsx|json|pdf|docx?|xlsx?|pptx?|png|jpe?g|gif|svg|webp|txt|csv|md|zip|exe|mp3|mp4|css|py)\\b(?:[/?#][^\\s<>\"')]*)?", // domains and file names
  "\\bv?\\d+(?:\\.\\d+)+[A-Za-z0-9.]*", // versions and decimals (v2.1, 3.5, 10.0.1)
  "[@#][\\w-]+", // @mentions and #tags
].join("|"), "gi");

function mask(text: string): { masked: string; tokens: string[] } {
  const tokens: string[] = [];
  const keep = (m: string) => { tokens.push(m); return `${tokens.length - 1}`; };
  let masked = text.replace(PROTECTED, keep);
  masked = masked.replace(ABBREVIATIONS, keep);
  return { masked, tokens };
}
const unmask = (text: string, tokens: string[]) => text.replace(/(\d+)/g, (_m, i) => tokens[Number(i)]);

function levenshtein(a: string, b: string): number {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++)
    dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return dp[a.length][b.length];
}
const matchCase = (orig: string, fix: string) =>
  orig === orig.toUpperCase() && orig.length > 1 ? fix.toUpperCase() : orig[0] === orig[0].toUpperCase() ? fix.charAt(0).toUpperCase() + fix.slice(1) : fix;

const THIRD_VERBS = ["go", "do", "want", "need", "like", "love", "take", "work", "play", "live", "know", "think", "say", "get", "come", "look", "use", "eat", "run", "walk", "talk", "speak", "write", "give", "tell", "ask", "try", "help", "start", "watch", "teach", "buy", "sell", "study", "cry", "enjoy", "hate", "feel", "seem", "become", "bring", "call", "keep", "leave", "meet", "move", "open", "pay", "show", "stay", "wear", "wait", "drive", "sleep", "sing", "swim", "fly", "pass", "fix", "finish", "carry"];
function thirdPerson(v: string): string {
  const l = v.toLowerCase();
  let out: string;
  if (/(s|sh|ch|x|z|o)$/.test(l)) out = l + "es";
  else if (/[^aeiou]y$/.test(l)) out = l.slice(0, -1) + "ies";
  else out = l + "s";
  return matchCase(v, out);
}

export function checkGrammar(text: string, spell?: SpellChecker | null, opts: GrammarOptions = {}): GrammarResult {
  const issues: GrammarIssue[] = [];
  const suggestions: GrammarIssue[] = [];
  const fix = (type: string, message: string) => issues.push({ type, message });
  const { masked, tokens } = mask(text);
  let t = masked;
  const variant = resolveVariant(masked.match(/\b[A-Za-z]{4,}\b/g) ?? [], spell, opts);

  // --- spacing -----------------------------------------------------------------------------
  if (/[ \t]{2,}/.test(t)) { fix("spacing", "Removed extra spaces."); t = t.replace(/[ \t]{2,}/g, " "); }
  if (/[ \t]+[,.!?;:](?!\.)/.test(t.replace(/\.\.\./g, ""))) {
    fix("spacing", "Removed spaces before punctuation.");
    t = t.replace(/[ \t]+([,.!?;:])(?!\.)/g, "$1");
  }
  if (/[a-z][.,!?;:][A-Za-z]/i.test(t)) {
    fix("spacing", "Added missing space after punctuation.");
    t = t.replace(/([A-Za-z])([.,!?;:])([A-Za-z])/g, "$1$2 $3");
  }

  // --- repeated words ----------------------------------------------------------------------
  t = t.replace(/\b([A-Za-z']+)\s+\1\b/gi, (m, w) => {
    if (/^(had|that|is|can)$/i.test(w)) return m; // "that that", "had had" are legitimate
    fix("repetition", `Removed repeated word "${w}".`);
    return w;
  });

  // --- contractions and common word confusions --------------------------------------------
  t = t.replace(/\b[A-Za-z]+\b/g, (w) => {
    const lower = w.toLowerCase();
    if (w !== lower && w !== w[0].toUpperCase() + w.slice(1).toLowerCase()) return w; // keep odd casing (iPhone, eBay)
    const c = CONTRACTIONS[lower];
    if (c && !AMBIGUOUS_CONTRACTIONS.has(lower)) { fix("grammar", `"${w}" → "${matchCase(w, c)}"`); return matchCase(w, c); }
    const irr = IRREGULAR_FIXES[lower];
    if (irr) { fix("grammar", `"${w}" → "${matchCase(w, irr)}"`); return matchCase(w, irr); }
    return w;
  });
  // Uncountable nouns written as plurals ("informations")
  t = t.replace(new RegExp(`\\b(${UNCOUNTABLE.split("|").filter(w => w !== "news" && w !== "traffic" && w !== "weather" && w !== "money").join("|")})s\\b`, "gi"), (m, w) => {
    fix("grammar", `"${m}" → "${w}" (not used in the plural)`);
    return w;
  });

  // Third-person singular: "She go to school" -> "She goes to school". Only a list of
  // common verbs, and not after "that/if/suggest..." where the bare form is correct (subjunctive).
  const THIRD = new RegExp(`\\b(he|she|it)\\s+(${THIRD_VERBS.join("|")})\\b`, "gi");
  t = t.replace(THIRD, (m, p, v, offset: number, whole: string) => {
    if (/\b(that|if|suggest|suggests|insist|demand|recommend|require|wish|lest|so|let|make|made|help)\s+$/i.test(whole.slice(Math.max(0, offset - 12), offset))) return m;
    const out = `${p} ${thirdPerson(v)}`;
    fix("grammar", `"${m}" → "${out}": verb agreement with "${p}"`);
    return out;
  });

  // "every days" -> "every day": "every" takes a singular noun (common time words only, so "every two days" is untouched).
  t = t.replace(/\b(every|each)\s+(day|week|month|year|morning|afternoon|evening|night|weekend|hour|minute|time|person|student|customer|user)s\b/gi, (m, q, n) => {
    fix("grammar", `"${m}" → "${q} ${n}": "${q}" is followed by a singular noun`);
    return `${q} ${n}`;
  });

  const pairRules: Array<[RegExp, string | ((m: string, ...g: string[]) => string), string]> = [
    [/\b(has|have|had)\s+went\b/gi, (m, v) => `${v} gone`, '"went" → "gone" after has/have/had'],
    [/\b(should|could|would|must|might)\s+of\b/gi, (m, v) => `${v} have`, '"of" → "have"'],
    [/\b(he|she|it)\s+don't\b/gi, (m, p) => `${p} doesn't`, '"don\'t" → "doesn\'t"'],
    [/\b(i)\s+has\b/gi, () => "I have", '"I has" → "I have"'],
    [/\b(you|we|they)\s+has\b/gi, (m, p) => `${p} have`, '"has" → "have"'],
    [/\b(he|she|it)\s+have\b(?!\s+to\b)/gi, (m, p) => `${p} has`, '"have" → "has"'],
    [new RegExp(`\\b(${UNCOUNTABLE})\\s+are\\b`, "gi"), (m, n) => `${n} is`, '"are" → "is" (uncountable noun)'],
    [/\b(their)\s+(going|is|are|not|here|there|always|being|coming|trying|doing|late|now|just|all|still|very|really)\b/gi,
      (m, _t, w) => `${/^[A-Z]/.test(m) ? "They're" : "they're"} ${w}`.replace("they're is", "there is").replace("They're is", "There is").replace("they're are", "there are").replace("They're are", "There are"), '"their" → "they\'re"'],
    [/\b(its)\s+(a|an|been|not|going|time|about|very|really|just|so|too|also|okay|ok|true|fine|nice|good|bad|hard|easy|clear|possible|my|your|the)\b/gi,
      (m, i, w) => `${i[0] === "I" ? "It's" : "it's"} ${w}`, '"its" → "it\'s"'],
    [/\b(your)\s+(welcome|going|not|doing|being|right|wrong|so|very|really|late|here|there)\b/gi,
      (m, y, w) => `${y[0] === "Y" ? "You're" : "you're"} ${w}`, '"your" → "you\'re"'],
    [/\b(it's)\s+(own|tail|name|owner|color|colour|shape|size|wings|head|paws|nest|purpose|surface)\b/gi,
      (m, i, w) => `${i[0] === "I" ? "Its" : "its"} ${w}`, '"it\'s" → "its" (possessive)'],
    [/\b(you're)\s+(message|email|name|account|order|house|car|phone|family|job|mother|father|team|company|website|own|friends?)\b/gi,
      (m, y, w) => `${y[0] === "Y" ? "Your" : "your"} ${w}`, '"you\'re" → "your" (possessive)'],
    [/\b(an)\s+(?![hx]|[A-Z]{2})([bcdfgjklmnpqrstvwyz]\w*)/g,
      (m, a, w) => `a ${w}`, '"an" → "a" before a consonant sound'],
    [/\b(An)\s+(?![hx]|[A-Z]{2})([bcdfgjklmnpqrstvwyz]\w*)/g,
      (m, a, w) => `A ${w}`, '"An" → "A" before a consonant sound'],
    [/\b(a)\s+(?!(?:uni|use|usu|eu|one|once|ubiq|uran|util|ukr|unanim|unif)\w*)([aeiou]\w*)/gi,
      (m, a, w) => `${a === "A" ? "An" : "an"} ${w}`, '"a" → "an" before a vowel sound'],
  ];
  for (const [re, rep, msg] of pairRules) {
    t = t.replace(re, (...args: any[]) => {
      const m = args[0] as string;
      const out = typeof rep === "function" ? (rep as any)(...args) : rep;
      if (out !== m) fix("grammar", `"${m}" → "${out}": ${msg}`);
      return out;
    });
  }
  // --- spelling ----------------------------------------------------------------------------
  const seenSuggest = new Set<string>();
  let checked = 0;
  t = t.replace(/\b[A-Za-z][A-Za-z']*[A-Za-z]\b/g, (w, offset: number, whole: string) => {
    const lower = w.toLowerCase();
    const common = COMMON_MISSPELLINGS[lower];
    if (common) { fix("spelling", `"${w}" → "${matchCase(w, common)}"`); return matchCase(w, common); }
    // A correct British spelling is never rewritten. US English chosen: it is listed as a suggestion only.
    const us = !/[A-Z]/.test(w.slice(1)) && w !== w.toUpperCase() ? ukToUs(w, spell) : null;
    if (us) {
      if (variant === "us" && !seenSuggest.has(lower) && !spell?.correct(lower)) {
        seenSuggest.add(lower);
        suggestions.push({ type: "spelling", message: `"${w}" is a UK spelling (US: "${matchCase(w, us)}"). Left as written; choose UK English above if you are writing British English.` });
      }
      return w;
    }
    if (!spell || w.length < 4) return w;
    if (/[A-Z]/.test(w.slice(1)) || w === w.toUpperCase()) return w; // iPhone, FreeDailyPro, NASA
    const sentenceStart = /(^|[.!?]\s+|\n)$/.test(whole.slice(0, offset));
    if (/^[A-Z]/.test(w) && !sentenceStart) return w; // probable proper noun
    if (spell.correct(w) || spell.correct(lower)) return w;
    if (++checked > 60) return w;
    const options = spell.suggest(w).slice(0, 4);
    const top = options[0];
    // Only auto-fix when the correction is unambiguous: no other candidate is equally close, and the fix
    // does not just swap a word ending (jumpd -> jumps would silently change the tense).
    const topDist = top ? levenshtein(lower, top.toLowerCase()) : 99;
    const rivals = options.filter((o) => !/\s/.test(o) && levenshtein(lower, o.toLowerCase()) <= topDist).length;
    const endingSwap = !!top && top.length === w.length && top.slice(0, -1).toLowerCase() === lower.slice(0, -1) && /[ds]$/.test(lower + top.toLowerCase());
    if (top && rivals === 1 && !endingSwap && !/\s/.test(top) && topDist <= 2 && top.length >= 3 && Math.abs(top.length - w.length) <= 2) {
      fix("spelling", `"${w}" → "${matchCase(w, top)}"`);
      return matchCase(w, top);
    }
    if (!seenSuggest.has(lower)) {
      seenSuggest.add(lower);
      suggestions.push({ type: "spelling", message: options.length ? `"${w}" may be misspelled. Did you mean ${options.map((o) => `"${o}"`).join(", ")}?` : `"${w}" is not in the dictionary. Check the spelling.` });
    }
    return w;
  });

  // --- capitalisation ----------------------------------------------------------------------
  if (/\bi\b/.test(t)) { fix("capitalization", 'Capitalized standalone "i".'); t = t.replace(/\bi\b/g, "I"); }
  t = t.replace(/(^|[.!?]\s+|\n\s*)([a-z])/g, (_m, prefix, letter) => { fix("capitalization", "Capitalized start of sentence."); return prefix + letter.toUpperCase(); });

  // --- repeated punctuation ----------------------------------------------------------------
  if (/([!?])\1{1,}/.test(t)) { fix("punctuation", "Simplified repeated punctuation."); t = t.replace(/([!?])\1+/g, "$1"); }

  // --- patterns we only flag --------------------------------------------------------------
  const flag = (re: RegExp, msg: (m: RegExpMatchArray) => string) => { const m = t.match(re); if (m) suggestions.push({ type: "grammar", message: msg(m) }); };
  flag(/\b(me and (?:him|her|them|\w+)) (was|were)\b/i, (m) => `"${m[0]}": consider "${m[1].replace(/^me and /i, "")} and I ${m[2].toLowerCase() === "was" ? "were" : m[2]}".`);
  flag(/\b(he|she|it|this|that)\s+(are|were)\b/i, (m) => `"${m[0]}": check subject-verb agreement.`);
  flag(/\b(they|we|you)\s+(is|was)\b/i, (m) => `"${m[0]}": check subject-verb agreement.`);
  flag(/\b(two|three|four|five|six|seven|eight|nine|ten|several|many|few|\d+)\s+(?:big |small |new |old |red |green |blue |good |bad )?(apple|car|book|day|week|month|year|person|child|man|woman|house|dog|cat|friend|student|problem)\b/i, (m) => `"${m[0]}": should the noun be plural?`);

  // Common confusions: always suggestions, never silent fixes (each depends on what the writer meant).
  const confuse = (re: RegExp, to: (m: RegExpExecArray) => string | null, why: string) => {
    for (const m of Array.from(t.matchAll(re))) {
      const fixTo = to(m as RegExpExecArray);
      if (fixTo) suggestions.push({ type: "grammar", message: `"${m[0]}" → "${fixTo}"? ${why}` });
    }
  };
  const COMPARATIVE = "taller|shorter|bigger|smaller|larger|better|worse|more|less|fewer|greater|higher|lower|older|younger|faster|slower|easier|harder|cheaper|longer|stronger|smarter|rather|other";
  confuse(new RegExp(`\\b(${COMPARATIVE})\\s+then\\b`, "gi"), (m) => `${m[1]} than`, 'Use "than" to compare ("taller than me"); "then" is about time.');
  confuse(/\b(will|would|could|can|may|might|not|didn't|doesn't|don't|won't|can't|shouldn't|wouldn't|couldn't)\s+effect\s+(the|my|your|our|their|his|her|its|this|that|how|what|whether|us|me|you|them|him|everyone|everything)\b/gi,
    (m) => `${m[1]} affect ${m[2]}`, '"Affect" is the verb ("it will affect the game"); "effect" is usually the noun.');
  confuse(/\b(the|an|no|any|side|negative|positive|major|big|great|little|lasting)\s+affect\s+(on)\b/gi,
    (m) => `${m[1]} effect ${m[2]}`, '"Effect" is the noun ("the effect on sales").');
  confuse(/\bless\s+(people|children|items|cars|books|students|users|customers|friends|days|hours|minutes|employees|options|things|mistakes|errors|calories|words|pages|points|times|problems|questions|visitors|members|participants|apples|men|women)\b/gi,
    (m) => `fewer ${m[1]}`, 'Use "fewer" with things you can count ("fewer people"), "less" with amounts ("less water").');
  confuse(/\b(will|would|could|can|may|might|to|not|didn't|doesn't|don't|won't|can't|shouldn't|wouldn't|couldn't|gonna)\s+loose\s+(the|my|your|our|their|his|her|its|a|an|weight|money|time|control|hope|track|sight|faith|touch|everything|all|it|them|you|us|me)\b/gi,
    (m) => `${m[1]} lose ${m[2]}`, '"Lose" means to misplace or fail to win; "loose" means not tight.');
  confuse(/\b(to|can't|cannot|couldn't|can|could|will|would|must|let|help)\s+breath\b/gi, (m) => `${m[1]} breathe`, '"Breathe" is the verb; "breath" is the noun.');
  confuse(/\b(a|deep|short|last|fresh|every)\s+breathe\b/gi, (m) => `${m[1]} breath`, '"Breath" is the noun ("a deep breath").');

  return { corrected: unmask(t, tokens), issues, suggestions, variant };
}

let spellPromise: Promise<SpellChecker | null> | null = null;
/** Loads the en_US dictionary once (about 0.5 MB, cached by the browser). Returns null if it can't be loaded. */
export function loadSpellChecker(): Promise<SpellChecker | null> {
  if (!spellPromise) {
    spellPromise = (async () => {
      try {
        const [{ default: nspell }, aff, dic] = await Promise.all([
          import("nspell"),
          fetch("/dict/en_US.aff").then((r) => { if (!r.ok) throw new Error("aff"); return r.text(); }),
          fetch("/dict/en_US.dic").then((r) => { if (!r.ok) throw new Error("dic"); return r.text(); }),
        ]);
        return nspell(aff, dic) as SpellChecker;
      } catch (err) {
        console.warn("Spelling dictionary could not be loaded; using the built-in word list only.", err);
        spellPromise = null;
        return null;
      }
    })();
  }
  return spellPromise;
}
